import * as React from 'react';
import {Helmet} from 'react-helmet';
import styled, {css} from 'styled-components';
import {useRoute} from 'react-router5';
import {observer} from 'mobx-react';
import {
  CourseObject,
  CourseStatus,
  deleteCourse,
  getCourses as getTeacherCourses,
} from 'api/course';
import {getCourses as getStudentCourses} from 'api/student/requests';
import Container from 'containers/Container';
import TeacherCoursePreview from 'components/TeacherCoursePreview';
import {ButtonVariant} from 'components/Button';
import Header from 'components/Header';
import HeaderSearch from 'components/HeaderSearch';
import UserStore, {Role} from 'stores/UserStore';
import ButtonLink from 'components/ButtonLink';
import CoursePreview from 'components/CoursePreview';
import {Filter} from 'components/Filter';
import validateResponse from 'helpers/validate-response';
import isError from 'helpers/is-error';
import {IconLoading} from 'icons';
import Radio from 'components/Radio';
import {FolderSelector} from 'components/FolderSelector';
import ViewChecker from "components/ViewChecker";
import useSelectedView, {CardView, SelectedViewType, Views} from "../hooks/use-selected-view";
import {Folder} from '../types';

const CoursesContainer = styled.div<{view: CardView}>`
  gap: 20px;
  justify-items: center;
  display: grid;
  margin-bottom: 120px;

  ${({view}) => {
    switch (view) {
      case CardView.Tiled: return css`
        grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
      `;
      case CardView.List: return css`
        grid-template-columns: 1fr;
        gap: 10px;
      `;
      case CardView.ShortList: return css`
        grid-template-columns: 1fr;
        gap: 10px;
      `;
    }
  }};
`;

export const StyledSelector = styled.div`
  margin-bottom: 14px;
`;

export const Filters = styled.div`
  margin-top: 20px;
  justify-content: space-between;
  display: flex;
`;

export const PageContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  box-sizing: border-box;
`;

/**
 * The courses page
 * @constructor
 */
function Courses(): React.ReactElement {
  const {route} = useRoute();
  const {coursesView}: Views = useSelectedView();
  const [view, setView] = React.useState(coursesView)
  const [folder, setFolder] = React.useState<Folder | undefined>(undefined);

  const [
    isLoaded,
    setLoaded,
  ] = React.useState<boolean>(false);

  const [
    courses,
    setCourses,
  ] = React.useState<CourseObject[]>([]);

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      fetchCourses();
    }, 600);
    return () => clearTimeout(timeout);
  }, [
    route.params.search,
    route.params.status,
    folder,
  ]);

  /**
   * Fetch courses.
   */
  function fetchCourses(): void {
    setLoaded(false);

    switch (UserStore.state?.role) {
      case Role.Student: {
        getStudentCourses().then((response) => {
          validateResponse(response);

          if (response && !isError(response)) {
            setCourses(response);
            setLoaded(true);
          }
        });
        break;
      }
      case Role.Teacher: {
        getTeacherCourses({
          my: '1',
          title: route.params.search,
          status: route.params.status,
          folder,
        }).then((response) => {
          validateResponse(response);

          if (response && !isError(response)) {
            setCourses(response);
            setLoaded(true);
          }
        });
        break;
      }
    }
  }

  /**
   * This function renders the page header.
   * @return {JSX.Element}
   */
  function renderHeader(): JSX.Element {
    return (
      <Header>
        <HeaderSearch
          inputSearch={{
            placeholder: 'Поиск по названию курса',
          }}
          renderAfter={UserStore.isTeacher() ? (
            <ButtonLink href="/courses/create" variant={ButtonVariant.Primary}>
              Создать курс
            </ButtonLink>
          ) : undefined}
        />
        <Filters>
          <Filter controls>
            <Filter.Group name="status" label="Публикация">
              {(group) => (
                [
                  (
                    <Filter.Field
                      type="radio"
                      label="Опубликовано"
                      value={CourseStatus.Public}>
                      {(field) => (
                        <Radio
                          name={group.name}
                          value={field.value}
                          checked={field.checked}
                          onChange={field.onChange}
                          label="Опубликовано"
                        />
                      )}
                    </Filter.Field>
                  ),
                  (
                    <Filter.Field
                      type="radio"
                      label="Не опубликовано"
                      value={CourseStatus.Draft}>
                      {(field) => (
                        <Radio
                          name={group.name}
                          value={field.value}
                          checked={field.checked}
                          onChange={field.onChange}
                          label="Не опубликовано"
                        />
                      )}
                    </Filter.Field>
                  ),
                ]
              )}
            </Filter.Group>
            <Filter.Group name="createdAt" label="Дата создания">
              {(group) => (
                [
                  (
                    <Filter.Field
                      type="radio"
                      label="Сегодня"
                      value="today">
                      {(field) => (
                        <Radio
                          name={group.name}
                          value={field.value}
                          checked={field.checked}
                          onChange={field.onChange}
                          label="Сегодня"
                        />
                      )}
                    </Filter.Field>
                  ),
                  (
                    <Filter.Field
                      type="radio"
                      label="Вчера"
                      value="tomorrow">
                      {(field) => (
                        <Radio
                          name={group.name}
                          value={field.value}
                          checked={field.checked}
                          onChange={field.onChange}
                          label="Вчера"
                        />
                      )}
                    </Filter.Field>
                  ),
                ]
              )}
            </Filter.Group>
          </Filter>
          <ViewChecker
            type={SelectedViewType.Courses}
            views={[
              CardView.Tiled,
              CardView.List,
              CardView.ShortList,
            ]}
            selected={view}
            onChange={(view) => setView(view)}
          />
        </Filters>
      </Header>
    );
  }

  /**
   * Render content.
   * @return {JSX.Element}
   */
  function renderContent(): JSX.Element {
    /**
     * The change event handler.
     * @param {Folder[]} folders
     */
    function handleChange(folders: Folder[]): void {
      setFolder(folders[0]);
    }

    return (
      <Container>
        <StyledSelector>
          <FolderSelector
            type="courses"
            onChange={handleChange}
          />
        </StyledSelector>
        {isLoaded ? (
          renderCourses()
        ) : <IconLoading />}
      </Container>
    );
  }

  /**
   * Render courses.
   * @return {JSX.Element}
   */
  function renderCourses(): JSX.Element {
    /**
     * Render the course.
     * @param {CourseObject} course
     * @param {number} index
     * @return {JSX.Element}
     */
    function renderCourse(
        course: CourseObject,
        index: number,
    ): JSX.Element {
      /**
       * This delete event handler.
       * @param {CourseObject<id>} id
       */
      function handleDelete(id: CourseObject['id']): void {
        deleteCourse(id).then((response) => {
          validateResponse(response);

          if (response && !isError(response)) {
            fetchCourses();
          }
        });
      }

      switch (UserStore.state?.role) {
        case Role.Student: return (
          <CoursePreview key={index} {...course} />
        );
        case Role.Teacher: return (
          <TeacherCoursePreview
            view={view}
            key={index}
            {...course}
            onDelete={handleDelete}
          />
        );
        default: return <React.Fragment />;
      }
    }

    /**
     * Render search not found.
     * @return {JSX.Element | undefined}
     */
    function renderSearchNotFound(): JSX.Element | undefined {
      const isParamsSearch = route.params.search?.length > 0;
      const message = `По запросу '${route.params.search}' - ничего не найдено`;

      if (isParamsSearch && !courses.length) {
        return (
          <React.Fragment>
            <h2>{message}</h2>
          </React.Fragment>
        )
      }
    }

    return renderSearchNotFound() || (
      <CoursesContainer view={view}>
        {courses.reverse().map((course, index) => (
          renderCourse(course, index)
        ))}
      </CoursesContainer>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>GrowPlace / Все курсы</title>
      </Helmet>
      <PageContentContainer>
        {renderHeader()}
        {renderContent()}
      </PageContentContainer>
    </React.Fragment>
  );
}

export default observer(Courses);
